import React, { useEffect, useMemo, useState } from "react";
import { authFetch, clearToken } from "./auth";
import RainbowText from "../components/RainbowText";
import AdminGallery from "./AdminGallery";

const STATUS_LABEL = { booked: "Reservado", featured: "Promoción" };

export default function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState("dates");
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [eventDate, setEventDate] = useState("");
  const [status, setStatus] = useState("booked");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);
  const canSubmit = !!eventDate && eventDate >= todayStr;

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch("/api/panel/dates");
      if (res.status === 401) {
        onLogout();
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Error");
      setDates(data.dates || []);
    } catch {
      setError("No se pudo cargar el calendario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      const res = await authFetch("/api/panel/dates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventDate, status, nota }),
      });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Error");
      setEventDate("");
      setNota("");
      await load();
    } catch {
      setError("No se pudo guardar la fecha");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      const res = await authFetch(`/api/panel/dates/${id}`, { method: "DELETE" });
      if (res.status === 401) {
        onLogout();
        return;
      }
      if (!res.ok) throw new Error("Error");
      setDates((prev) => prev.filter((d) => d.Id !== id));
    } catch {
      setError("No se pudo liberar la fecha");
    }
  };

  const logout = () => {
    clearToken();
    onLogout();
  };

  return (
    <div className="admin-shell">
      <div className="admin-topbar">
        <div className="brand-mini">
          <span className="dot" />
          <RainbowText text="LoREMi G" className="wordmark" />
          <span className="admin-title-suffix">· Admin</span>
        </div>
        <button className="admin-link-btn" onClick={logout}>
          Cerrar sesión
        </button>
      </div>

      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "dates"}
          className={"admin-tab" + (tab === "dates" ? " active" : "")}
          onClick={() => setTab("dates")}
        >
          Calendario
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "gallery"}
          className={"admin-tab" + (tab === "gallery" ? " active" : "")}
          onClick={() => setTab("gallery")}
        >
          Galería
        </button>
      </div>

      {tab === "gallery" && <AdminGallery onLogout={onLogout} />}

      {tab === "dates" && (
        <div className="admin-grid">
          <form className="admin-card" onSubmit={submit}>
            <h3>Marcar una fecha</h3>

            <label className="admin-field">Fecha</label>
            <input
              className="admin-input"
              type="date"
              min={todayStr}
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
            />

            <label className="admin-field">Estado</label>
            <select className="admin-input" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="booked">Reservado (ya no se puede apartar)</option>
              <option value="featured">Promoción (se destaca en el calendario)</option>
            </select>

            <label className="admin-field">Nota (opcional)</label>
            <input
              className="admin-input"
              placeholder="Ej. Cumpleaños familia Gómez"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />

            {error && <div className="admin-error">{error}</div>}

            <button className="admin-btn" type="submit" disabled={saving || !canSubmit}>
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </form>

          <div className="admin-card admin-list-card">
            <h3>Fechas marcadas</h3>
            {loading && <p className="admin-empty">Cargando…</p>}
            {!loading && dates.length === 0 && (
              <p className="admin-empty">Todavía no hay fechas marcadas.</p>
            )}
            {!loading && dates.length > 0 && (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Estado</th>
                    <th>Nota</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {dates.map((d) => (
                    <tr key={d.Id}>
                      <td>{new Date(d.EventDate).toLocaleDateString("es-MX", { timeZone: "UTC" })}</td>
                      <td>
                        <span className={`admin-badge admin-badge-${d.Status}`}>
                          {STATUS_LABEL[d.Status] || d.Status}
                        </span>
                      </td>
                      <td>{d.Nota || "—"}</td>
                      <td>
                        <button className="admin-link-btn" onClick={() => remove(d.Id)}>
                          Liberar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
